import React, { useState } from 'react';

import { ButtonBase, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  AlineButton,
  AlineTooltip,
  StorableIcon,
  ToggleSwitch,
  UploadFileVersionPicker,
} from 'src/components';
import { AddIcon } from 'src/components/icons/AddIcon';
import { DocumentIcon } from 'src/components/icons/DocumentIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { UploadIcon } from 'src/components/icons/UploadIcon';
import { WorkflowDoneSuccessIcon } from 'src/components/icons/WorkflowDoneSuccessIcon';
import { Apollo } from 'src/gql/generated';
import type { StaticFileVersionWithLocation } from 'src/helpers';
import { pdfMimeTypes } from 'src/helpers';
import {
  useApplySignedVersionOutsideAline,
  useAreWorkflowStepsEditable,
  useDeleteFileVersion,
  useGetWorkflowFiles,
  useGetWorkflowSimple,
} from 'src/hooks';
import { SignOutsideAlineRepositoryDialog } from 'src/workflow/steps/SignOutsideAlineRepositoryDialog';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  buttonBody: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
  },
  docList: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  docName: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
  },
  docRow: {
    alignItems: 'center',
    display: 'flex',
    height: 60,
    justifyContent: 'space-between',
  },
  empty: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  menuIcon: {
    marginRight: theme.spacing(1),
  },
  paper: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    width: '100%',
  },
  signed: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    padding: theme.spacing(0.5),
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  toggle: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  tooltip: { width: 'fit-content' },
  uploadButton: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    border: `1px dashed ${theme.appColors.rule}`,
    padding: theme.spacing(1),
  },
  uploadSignedVersionText: {
    fontWeight: 600,
  },
  wrapper: {
    display: 'flex',
    width: '100%',
  },
}));

export function SignOutsideAline({ workflowId }: { workflowId: UUID }) {
  const { classes } = useStyles();

  return (
    <div className={classes.wrapper}>
      <SignOutsideAlineBody workflowId={workflowId} />
    </div>
  );
}

export function SignOutsideAlineBody({ workflowId }: { workflowId: UUID }) {
  const { classes } = useStyles();
  const { workflow } = useGetWorkflowSimple(workflowId);
  const { workflowFiles } = useGetWorkflowFiles(workflowId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [finishSigningOutsideAline, { loading: finishing }] =
    Apollo.useFinishSigningOutsideAlineMutation();
  // General attachments are never signed, so they stay out of the list and out of its gating.
  const signingFiles = workflowFiles.filter(
    file => file.documentRole === Apollo.WorkflowFileDocumentRole.Signing
  );
  const allFilesSigned = signingFiles.every(file => file.document.isSigned);
  const isSigSectionDone =
    workflow?.state == Apollo.WorkflowState.Signed ||
    workflow?.state == Apollo.WorkflowState.Complete;
  const finishDisabledReason = !allFilesSigned
    ? 'Upload signed versions for each document to finish'
    : !signingFiles.length
      ? 'Add documents to sign'
      : '';

  if (isSigSectionDone)
    return (
      <div className={classes.paper}>
        <div className={classes.signed}>
          <WorkflowDoneSuccessIcon />
          <Typography align="center" variant="subtitle1">
            This workflow was signed outside Aline. All signed versions were manually uploaded.
          </Typography>
        </div>
      </div>
    );

  return (
    <div className={classes.paper}>
      <Typography className={classes.title} variant="subtitle2">
        Workflow Documents
      </Typography>
      <div className={classes.docList}>
        <SignOutsideAlineDocument workflowFiles={signingFiles} workflowId={workflowId} />
      </div>
      <AlineTooltip arrow placement="top" title={finishDisabledReason}>
        <div className={classes.tooltip}>
          <AlineButton
            color="primary"
            disabled={Boolean(finishDisabledReason)}
            loading={finishing}
            variant="contained"
            onClick={async () => {
              await finishSigningOutsideAline({
                variables: {
                  workflowId,
                },
              });
            }}
          >
            Finish
          </AlineButton>
        </div>
      </AlineTooltip>
    </div>
  );
}

type SignOutsideAlineWorkflowFile = Pick<Apollo.WorkflowFile, 'id' | 'documentRole'> & {
  document: Pick<Apollo.Document, 'id' | '__typename' | 'name' | 'isSigned' | 'variant'> & {
    latestVersion:
      | Pick<Apollo.DocumentVersion, 'id' | '__typename'>
      | StaticFileVersionWithLocation;
  };
};

function SignOutsideAlineDocument({
  workflowFiles,
  workflowId,
}: {
  workflowFiles: SignOutsideAlineWorkflowFile[];
  workflowId: UUID;
}) {
  const { classes } = useStyles();

  if (!workflowFiles.length)
    return (
      <div className={classes.empty}>
        <Typography>
          No signing documents. Add signing documents to complete signing outside Aline.
        </Typography>
      </div>
    );

  return (
    <>
      {workflowFiles.map(file => {
        return (
          <div key={file.id} className={classes.docRow}>
            <div className={classes.docName}>
              <StorableIcon storable={file.document} />
              <Typography>{file.document.name}</Typography>
            </div>
            <SignOutsideAlineUploadButton document={file.document} workflowId={workflowId} />
          </div>
        );
      })}
    </>
  );
}

interface SignOutsideAlineDocument extends Pick<Apollo.Document, 'name' | 'id' | 'isSigned'> {
  latestVersion: Pick<Apollo.FileVersion, 'id'>;
}

export function SignOutsideAlineUploadButton({
  document,
  workflowId,
}: {
  document: SignOutsideAlineDocument;
  workflowId: UUID;
}) {
  const { classes } = useStyles();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [repositoryOpen, setRepositoryOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const { deleteFileVersion, loading: deletingFileVersion } = useDeleteFileVersion();
  const { applySignedVersionOutsideAline } = useApplySignedVersionOutsideAline();

  if (document.isSigned)
    return (
      <AlineTooltip arrow placement="top" title="Remove signed version">
        <div>
          <IconButton
            disabled={deletingFileVersion}
            size="large"
            onClick={async () => {
              await deleteFileVersion({
                documentId: document.id,
                isDeletingLatestVersion: true,
                options: { variables: { id: document.latestVersion.id } },
              });
            }}
          >
            <TrashcanIcon />
          </IconButton>
        </div>
      </AlineTooltip>
    );

  return (
    <>
      <ButtonBase
        className={classes.uploadButton}
        onClick={event => setMenuAnchorEl(event.currentTarget)}
      >
        <div className={classes.buttonBody}>
          <AddIcon />
          <Typography noWrap className={classes.uploadSignedVersionText}>
            Upload signed version
          </Typography>
        </div>
      </ButtonBase>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            setUploadOpen(true);
          }}
        >
          <UploadIcon className={classes.menuIcon} />
          <Typography>Upload</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            setRepositoryOpen(true);
          }}
        >
          <DocumentIcon className={classes.menuIcon} />
          <Typography>Choose from repository</Typography>
        </MenuItem>
      </Menu>
      <UploadFileVersionPicker
        domContainer={`picker-${document.id}`}
        open={uploadOpen}
        pickerOpts={{
          accept: [...pdfMimeTypes],
          createFromUpload: async fileLocation => {
            const { data } = await applySignedVersionOutsideAline(document.id, {
              variables: {
                input: {
                  documentId: document.id,
                  props: { fileLocation },
                  workflowId,
                },
              },
            });
            return data?.applySignedVersionOutsideAline.fileVersion;
          },
          documentId: document.id,
          onClose: () => setUploadOpen(false),
        }}
      />
      <SignOutsideAlineRepositoryDialog
        open={repositoryOpen}
        targetDocument={document}
        workflowId={workflowId}
        onClose={() => setRepositoryOpen(false)}
      />
    </>
  );
}

export function SwitchToSignOutsideAline({
  className,
  toggle,
  toggled,
  workflowId,
}: {
  toggle: () => void;
  toggled: boolean;
  workflowId: UUID;
  className?: string;
}): JSX.Element | null {
  const { classes } = useStyles();
  const { areStepsEditable, loading, workflow } = useAreWorkflowStepsEditable(workflowId);
  const isTemplateWorkflow = workflow?.variant === Apollo.WorkflowVariant.Template;

  if (loading || isTemplateWorkflow || !areStepsEditable) return null;

  return (
    <div className={clsx(classes.toggle, className)}>
      <ToggleSwitch toggle={toggle} toggled={toggled} />
      <Typography variant="body1">Signed outside of Aline?</Typography>
    </div>
  );
}
